import React from 'react';
import { useTranslation } from 'react-i18next';
import { Message } from './types';
import { CATALOG_CONNECTION_KINDS, catalogStatusFallback, parseCatalogStatus } from './StreamingIndicator';

/**
 * Page-level catalog connection notice. A catalog login (XENTRY e-mail OTP,
 * a Microcat licence seat) can block a turn for minutes; the server keeps the
 * turn alive and continues on its own once it lands, so nothing is paused on
 * the client — the wait just should not sit inside a chat bubble with a fake
 * progress bar. The streaming bubble keeps its search steps; this strip owns
 * the four connection kinds only.
 */
interface CatalogStatusStripProps {
  messages: Message[];
}

const CONNECTED_LINGER_MS = 4000;

/** Catalog implementation slug → name a customer recognises. */
const CATALOG_NAMES: Record<string, string> = {
  xentry: 'Mercedes-Benz (XENTRY)',
  microcat: 'Microcat',
  snaponepc: 'Snap-on EPC',
  saicmotor: 'SAIC / MG',
};

export function latestConnectionEvent(messages: Message[]): { key: string; type: string; content: string } | null {
  const streaming = messages.find(m => m.isStreaming && m.events?.length);
  if (!streaming || !streaming.events) return null;
  for (let i = streaming.events.length - 1; i >= 0; i--) {
    const ev = streaming.events[i];
    if (CATALOG_CONNECTION_KINDS.has(ev.type)) return { key: `${streaming.id}|${i}`, type: ev.type, content: ev.content };
  }
  return null;
}

const CatalogStatusStrip: React.FC<CatalogStatusStripProps> = ({ messages }) => {
  const { t } = useTranslation();
  const event = latestConnectionEvent(messages);
  const isDone = !!event && (event.type === 'catalog_connected' || event.type === 'catalog_reconnected');
  const [hidden, setHidden] = React.useState(false);
  const [now, setNow] = React.useState(Date.now());
  const startedAtRef = React.useRef<number | null>(null);

  // Elapsed clock while connecting; the "connected" line lingers briefly then hides.
  React.useEffect(() => {
    if (!event) { startedAtRef.current = null; setHidden(false); return; }
    if (isDone) {
      const timer = setTimeout(() => setHidden(true), CONNECTED_LINGER_MS);
      return () => clearTimeout(timer);
    }
    setHidden(false);
    if (startedAtRef.current === null) startedAtRef.current = Date.now();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [event?.key, isDone]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!event || hidden) return null;

  const status = parseCatalogStatus(event.content);
  const elapsed = startedAtRef.current ? Math.floor((now - startedAtRef.current) / 1000) : 0;
  const seconds = event.type === 'catalog_reconnecting' ? Math.max(0, status.seconds - elapsed) : status.seconds;
  const catalogName = CATALOG_NAMES[status.catalog] || t('streamingEvents.catalog_generic_name', { defaultValue: 'the manufacturer catalog' });
  const busy = event.type === 'catalog_reconnecting';
  const headline = isDone
    ? t('streamingEvents.catalog_strip_connected', { defaultValue: 'Connected to {{catalog}}. Searching your parts now…', catalog: catalogName })
    : busy
      ? t('streamingEvents.catalog_reconnecting', { defaultValue: catalogStatusFallback(event.type, seconds), seconds, catalog: catalogName })
      : t('streamingEvents.catalog_strip_connecting', { defaultValue: 'Connecting to {{catalog}} right now…', catalog: catalogName });
  const detail = busy
    ? t('streamingEvents.catalog_strip_busy_hint', { defaultValue: 'Another session holds the catalog licence. We retry automatically and then continue your search.' })
    : t('streamingEvents.catalog_connecting_hint', { defaultValue: 'We are signing in to the catalog before searching. This can take a few minutes. Your parts search starts automatically as soon as it is connected.' });
  const clock = elapsed >= 60 ? `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}` : `${elapsed}s`;

  return (
    <div
      role="status"
      data-testid="catalog-status-strip"
      className={`sticky top-0 z-20 mb-4 flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm shadow-sm ${
        isDone ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}
    >
      <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${isDone ? 'bg-emerald-500' : 'bg-amber-400 animate-pulse'}`} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{headline}</div>
        {!isDone && <div className="mt-0.5 text-xs opacity-80">{detail}</div>}
        {!isDone && (
          <div className="mt-1.5 flex items-center gap-2 text-[11px] font-medium">
            <span className="rounded-full bg-amber-200/70 px-2 py-0.5">1 · {t('streamingEvents.catalog_step_connecting', { defaultValue: 'Connecting to catalog' })}</span>
            <span className="opacity-60">→</span>
            <span className="rounded-full border border-amber-200 px-2 py-0.5 opacity-60">2 · {t('streamingEvents.catalog_step_searching', { defaultValue: 'Searching your parts' })}</span>
          </div>
        )}
      </div>
      {!isDone && <span className="text-xs tabular-nums opacity-70">{clock}</span>}
    </div>
  );
};

export default CatalogStatusStrip;
